<template>
    <div class="form-container card shadow-sm p-3">
      <!-- Cabeçalho -->
      <div class="header d-flex justify-content-between align-items-center">
        <h4 class="form-title">
          {{ isEditing ? "Editar Período de Avaliação nº" : "Novo Período de Avaliação" }}
          <span v-if="isEditing">{{ idPeriodo }}</span>
        </h4>
        <button class="btn btn-secondary" @click="cancelar">Voltar</button>
      </div>
  
      <div class="spacer"></div>
  
      <!-- Formulário -->
      <b-form @submit.prevent="submitFormulario">
        <!-- Seção: Dados do Período de Avaliação -->
        <div class="form-section">
            <div class="form-group">
                <label for="avaliacao">
                Avaliação <span class="required">*</span>
                </label>
                <input
                type="text"
                id="avaliacao"
                class="form-control"
                v-model="avaliacao"
                placeholder="Informe o nome da avaliação"
                required
                />
            </div>
            <!-- Agrupando campos de data na mesma linha -->
            <div class="row">
                <div class="col-md-2 form-group">
                <label for="dataInicio">
                    Início <span class="required">*</span>
                </label>
                <input
                    type="date"
                    id="dataInicio"
                    class="form-control"
                    v-model="dataInicio"
                    required
                />
                </div>
                <div class="col-md-2 form-group">
                <label for="dataFim">
                    Fim <span class="required">*</span>
                </label>
                <input
                    type="date"
                    id="dataFim"
                    class="form-control"
                    v-model="dataFim"
                    required
                />
                </div>
            </div>
        </div>
  
        <!-- Seção: Associação de Níveis e Formulários de Prova -->
        <div class="form-section">
          <h5 class="section-title">Selecione o Formulário de Prova para cada Nível</h5>
          <table class="table table-borderless align-middle mb-0">
            <thead>
              <tr>
                <th style="width: 5%;">#</th>
                <th style="width: 40%;">Nível</th>
                <th style="width: 55%;">Formulário de Prova <span class="required">*</span></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(assoc, index) in niveisAssociacoes"
                :key="assoc.nivel_id"
              >
                <td>{{ index + 1 }}</td>
                <td>{{ assoc.nivel_nome }}</td>
                <td>
                  <select
                    v-model="assoc.avaliacoes_formularios_id"
                    class="form-control"
                    required
                  >
                    <option value="" disabled>Selecione o formulário de prova</option>
                    <option
                      v-for="opcao in opcoesFormularios"
                      :key="opcao.id"
                      :value="opcao.id"
                    >
                      {{ opcao.formulario }}
                    </option>
                  </select>
                </td>
              </tr>
              <tr v-if="niveisAssociacoes.length === 0">
                <td colspan="3" class="text-center">Nenhum nível disponível.</td>
              </tr>
            </tbody>
          </table>
        </div>
  
        <!-- Botões de Ação -->
        <div class="form-actions d-flex justify-content-end">
          <button
            type="submit"
            :class="[
              'btn',
              isSubmitting ? 'btn-success' : 'btn-primary',
              'me-2'
            ]"
            :disabled="!formValid || isSubmitting"
          >
            <i :class="isSubmitting ? 'bi bi-hourglass-split me-1' : 'bi bi-save me-1'"></i>
            {{ isSubmitting ? 'Salvando…' : 'Salvar' }}
          </button>
          <button
            type="button"
            class="btn btn-secondary"
            @click="cancelar"
            :disabled="isSubmitting"
          >
            <i class="bi bi-x-circle me-1"></i>Cancelar
          </button>
        </div>
      </b-form>
    </div>
  </template>
  
  <script>
  import { ref, onMounted, computed } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { BForm } from "bootstrap-vue-next";
  import axios from "@/services/axios";
  
  export default {
    name: "FormularioPeriodoAvaliacao",
    components: { BForm },
    setup() {
      const route = useRoute();
      const router = useRouter();
  
      // Dados da Seção 1
      const avaliacao = ref("");
      const dataInicio = ref("");
      const dataFim = ref("");
  
      // Dados da Seção 2 – Associação de níveis com formulário de prova
      // Este array conterá objetos com: nivel_id, nivel_nome e avaliacoes_formularios_id (inicialmente vazio ou predefinido)
      const niveisAssociacoes = ref([]);
  
      // Opções para os formulários de prova
      const opcoesFormularios = ref([]);
  
      // Variáveis de controle de modo de edição
      const isEditing = ref(false);
      const idPeriodo = ref(null);
  
      
      const isSubmitting = ref(false);

      // Validação do formulário

      const formValid = computed(() => {
        // Verifica se os campos obrigatórios estão preenchidos
        if (!avaliacao.value.trim() || !dataInicio.value || !dataFim.value) return false;
        // Verifica se para cada associação foi selecionado um formulário
        for (const assoc of niveisAssociacoes.value) {
          if (!assoc.avaliacoes_formularios_id) return false;
        }
        return true;
      });
  
      // Carregar os níveis ativos (endpoint /cadastros/niveis-turmas/todos)
      const carregarNiveis = async () => {
        try {
          const response = await axios.get("/cadastros/niveis-turmas/todos", {
            params: { ativo: true }
          });
          // Inicialmente, para um novo cadastro, popula o array com todos os níveis disponíveis
          // Cada objeto conterá o id do nível, o nome (campo 'nivel') e um campo para o formulário (inicialmente vazio)
          niveisAssociacoes.value = response.data.data.map(nivel => ({
            nivel_id: nivel.id,
            nivel_nome: nivel.nivel,
            avaliacoes_formularios_id: "" // campo a ser preenchido pelo usuário
          }));
        } catch (error) {
          console.error("Erro ao carregar níveis:", error.message);
        }
      };
  
      // Carregar os formulários de prova (endpoint /configuracoes/formularios-avaliacoes/todos)
      const carregarFormularios = async () => {
        try {
          const response = await axios.get("/configuracoes/formularios-avaliacoes/todos");
          opcoesFormularios.value = response.data.data;
        } catch (error) {
          console.error("Erro ao carregar formulários de prova:", error.message);
        }
      };
  
      function formatDateForInput(dateStr) {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        return date.toISOString().slice(0, 10);
      }

      // Se estiver em modo de edição, carregar os dados do período e suas associações
      const carregarPeriodoParaEdicao = async (id) => {
        try {
            const response = await axios.get(`/cadastros/avaliacoes-periodos/${id}`);
            const data = response.data;
            idPeriodo.value = data.id;
            avaliacao.value = data.nome;
            // Converte as datas para o formato "yyyy-MM-dd"
            dataInicio.value = formatDateForInput(data.data_inicio);
            dataFim.value = formatDateForInput(data.data_fim);

            // Se houver associações, verificar e mesclar com os níveis carregados
            if (data.niveis && Array.isArray(data.niveis)) {
            // Assegura que os níveis ativos já foram carregados
            await carregarNiveis();
            // Para cada associação vinda do back-end, atualiza o dropdown correspondente
            data.niveis.forEach((assocEdicao) => {
                const index = niveisAssociacoes.value.findIndex(
                (nivel) => nivel.nivel_id === assocEdicao.nivel_id
                );
                if (index !== -1) {
                niveisAssociacoes.value[index].avaliacoes_formularios_id = assocEdicao.avaliacoes_formularios_id;
                } else {
                console.warn(`DEBUG - Nível com id ${assocEdicao.nivel_id} não encontrado no array de níveis ativos.`);
                }
            });
            } 
        } catch (error) {
            console.error("Erro ao carregar período para edição:", error.message);
        }
      };
  
      const cancelar = () => {
        router.push("/cadastros/avaliacoes-periodos");
      };
  
      const submitFormulario = async () => {
        // evita duplo clique
        if (isSubmitting.value) return;
        // valida
        if (!formValid.value) return;

        isSubmitting.value = true;

        const payload = {
          nome: avaliacao.value,
          data_inicio: dataInicio.value,
          data_fim: dataFim.value,
          aplicada: false,
          niveis: niveisAssociacoes.value.map(assoc => ({
            nivel_id: assoc.nivel_id,
            avaliacoes_formularios_id: assoc.avaliacoes_formularios_id
          }))
        };
        try {
          if (isEditing.value) {
            await axios.put(`/cadastros/avaliacoes-periodos/${idPeriodo.value}`, payload);
          } else {
            await axios.post(`/cadastros/avaliacoes-periodos`, payload);
          }
          router.push("/cadastros/avaliacoes-periodos");
        } catch (error) {
          console.error("Erro ao salvar período de avaliação:", error.message);
        } finally {
          isSubmitting.value = false;
        }
      };
  
      onMounted(async () => {
        await carregarFormularios();
        if (route.params.id) {
            isEditing.value = true;
            await carregarNiveis(); // garante o carregamento dos níveis antes de mesclar dados
            await carregarPeriodoParaEdicao(route.params.id);
        } else {
            await carregarNiveis();
        }
      });
  
      return {
        avaliacao,
        dataInicio,
        dataFim,
        niveisAssociacoes,
        opcoesFormularios,
        isEditing,
        idPeriodo,
        formValid,
        isSubmitting,
        cancelar,
        submitFormulario
      };
    },
  };
  </script>
  
  <style scoped>
  .form-container {
    margin: 20px auto;
    width: 90%;
  }
  .header {
    margin-bottom: 10px;
  }
  .form-title {
    font-size: 1.25rem;
    font-weight: bold;
  }
  .spacer {
    margin-bottom: 20px;
  }
  .form-section {
    background: white;
    padding: 15px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
    margin-bottom: 1rem;
  }
  .form-group {
    margin-bottom: 1rem;
  }
  .section-title {
    font-size: 1rem;
    font-weight: bold;
    margin-bottom: 10px;
  }
  .form-actions {
    margin-top: 20px;
  }
  .required {
    color: red;
  }
  </style>
  